import { useState } from "react";
import {
  makeStyles,
  tokens,
  Button,
  Field,
  Input,
  Textarea,
  Switch,
  Text,
  Card,
} from "@fluentui/react-components";
import { Add24Regular, Delete24Regular, Eye24Regular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  wrap: { marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" },
  intro: { color: tokens.colorNeutralForeground3 },
  card: { display: "flex", flexDirection: "column", gap: "10px", padding: "12px" },
  row: { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" },
  grow: { flexGrow: 1, minWidth: "200px" },
  code: { fontFamily: "Consolas, monospace", fontSize: "12px" },
  select: { padding: "5px", borderRadius: "4px", border: `1px solid ${tokens.colorNeutralStroke1}` },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  preview: { padding: "10px", border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: "4px", background: "#fff" },
});

const listVal = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const dtLocal = (v) => (v || "").slice(0, 16); // ISO -> input[type=datetime-local]

export default function CampaignsTab({ value, onChange }) {
  const styles = useStyles();
  const campaigns = value.campaigns || [];
  const [previewIdx, setPreviewIdx] = useState(-1);

  const setCampaigns = (next) => onChange({ ...value, campaigns: next });
  const update = (i, patch) => setCampaigns(campaigns.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const updateSchedule = (i, patch) => update(i, { schedule: { ...(campaigns[i].schedule || {}), ...patch } });
  const updateTarget = (i, patch) => update(i, { target: { ...(campaigns[i].target || {}), ...patch } });
  const add = () =>
    setCampaigns([
      ...campaigns,
      {
        id: `campaign-${Date.now()}`,
        name: "New campaign",
        enabled: true,
        bannerHtml: '<a href="https://www.davey.com"><img src="https://localhost:3000/assets/davey-logo.png" alt="Davey" width="320" style="display:block;border:0;" /></a>',
        schedule: { start: "", end: "" },
        target: { audience: "all", departments: [], groups: [] },
      },
    ]);
  const remove = (i) => setCampaigns(campaigns.filter((_, idx) => idx !== i));

  return (
    <div className={styles.wrap}>
      <Text className={styles.intro}>
        Campaigns add a marketing banner <b>below</b> the signature, but only while their schedule
        window is active and the targeting matches. Set start/end dates to time-box a promo — it
        turns on and off automatically.
      </Text>

      {campaigns.map((c, i) => {
        const t = c.target || {};
        const s = c.schedule || {};
        return (
          <Card key={c.id || i} className={styles.card}>
            <div className={styles.head}>
              <Text weight="semibold">{c.name || "(campaign)"}</Text>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Switch label="Enabled" checked={c.enabled !== false} onChange={(_, d) => update(i, { enabled: d.checked })} />
                <Button appearance="subtle" icon={<Delete24Regular />} onClick={() => remove(i)} />
              </div>
            </div>

            <div className={styles.row}>
              <Field label="Name" className={styles.grow}>
                <Input value={c.name || ""} onChange={(_, d) => update(i, { name: d.value })} />
              </Field>
              <Field label="Starts">
                <Input type="datetime-local" value={dtLocal(s.start)} onChange={(_, d) => updateSchedule(i, { start: d.value })} />
              </Field>
              <Field label="Ends">
                <Input type="datetime-local" value={dtLocal(s.end)} onChange={(_, d) => updateSchedule(i, { end: d.value })} />
              </Field>
            </div>

            <div className={styles.row}>
              <Field label="Audience">
                <select className={styles.select} value={t.audience || "all"} onChange={(e) => updateTarget(i, { audience: e.target.value })}>
                  <option value="all">all</option>
                  <option value="internal">internal</option>
                  <option value="external">external</option>
                </select>
              </Field>
              <Field label="Department(s), comma-separated" className={styles.grow}>
                <Input value={listVal(t.departments)} onChange={(_, d) => updateTarget(i, { departments: toList(d.value) })} />
              </Field>
              <Field label="Group(s), comma-separated" className={styles.grow}>
                <Input value={listVal(t.groups)} onChange={(_, d) => updateTarget(i, { groups: toList(d.value) })} />
              </Field>
            </div>

            <Field label="Banner HTML">
              <Textarea className={styles.code} resize="vertical" rows={4} value={c.bannerHtml || ""} onChange={(_, d) => update(i, { bannerHtml: d.value })} />
            </Field>

            <div>
              <Button icon={<Eye24Regular />} onClick={() => setPreviewIdx(previewIdx === i ? -1 : i)}>
                {previewIdx === i ? "Hide preview" : "Preview banner"}
              </Button>
            </div>
            {previewIdx === i && <div className={styles.preview} dangerouslySetInnerHTML={{ __html: c.bannerHtml || "" }} />}
          </Card>
        );
      })}

      <Button icon={<Add24Regular />} onClick={add}>Add campaign</Button>
    </div>
  );
}
